/*
 * Multi Featured Products Section
 * Custom element for drag-to-scroll and arrow navigation functionality
 */

if (!customElements.get('multi-featured-products')) {
  class MultiFeaturedProducts extends HTMLElement {
    constructor() {
      super();
      this.isDown = false;
      this.startX = 0;
      this.scrollLeft = 0;
      this.isDragging = false;

      // Bind methods for proper event listener removal
      this.handleMouseUp = this.handleMouseUp.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
    }

    connectedCallback() {
      this.scrollContainer = this.querySelector('.multi-featured-products__scroll');
      this.wrapper = this.querySelector('.multi-featured-products__wrapper');
      this.prevArrow = this.querySelector('.multi-featured-products__arrow--prev');
      this.nextArrow = this.querySelector('.multi-featured-products__arrow--next');

      if (this.scrollContainer) {
        this.initDragScroll();
        this.initArrows();
        this.checkOverflow();
        this.scrollToCenter();
        this.updateArrowStates();
        this.updateFadeEdges();

        // Re-check on resize
        window.addEventListener('resize', this.debounce(() => {
          this.checkOverflow();
          this.scrollToCenter();
          this.updateArrowStates();
          this.updateFadeEdges();
        }, 100));
      }
    }

    disconnectedCallback() {
      // Clean up global listeners when element is removed
      document.removeEventListener('mouseup', this.handleMouseUp);
      document.removeEventListener('mousemove', this.handleMouseMove);
    }

    initDragScroll() {
      const container = this.scrollContainer;

      // Mouse down - start drag
      container.addEventListener('mousedown', (e) => {
        // Only respond to left mouse button
        if (e.button !== 0) return;

        this.isDown = true;
        this.isDragging = false;
        container.style.cursor = 'grabbing';
        container.style.userSelect = 'none';
        this.startX = e.pageX - container.offsetLeft;
        this.scrollLeft = container.scrollLeft;

        // Add global listeners to catch mouseup/mousemove anywhere
        document.addEventListener('mouseup', this.handleMouseUp);
        document.addEventListener('mousemove', this.handleMouseMove);
      });

      // Prevent clicks on links while dragging
      container.addEventListener('click', (e) => {
        if (this.isDragging) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      // Update arrow states and fade edges on scroll
      container.addEventListener('scroll', this.debounce(() => {
        this.updateArrowStates();
        this.updateFadeEdges();
      }, 50));
    }

    handleMouseUp() {
      if (!this.isDown) return;

      this.isDown = false;
      this.scrollContainer.style.cursor = 'grab';
      this.scrollContainer.style.userSelect = '';

      // Remove global listeners
      document.removeEventListener('mouseup', this.handleMouseUp);
      document.removeEventListener('mousemove', this.handleMouseMove);

      // Reset dragging state after a short delay to allow click events
      setTimeout(() => {
        this.isDragging = false;
      }, 10);
    }

    handleMouseMove(e) {
      if (!this.isDown) return;

      e.preventDefault();
      const x = e.pageX - this.scrollContainer.offsetLeft;
      const walk = (x - this.startX) * 1.5;

      if (Math.abs(walk) > 5) {
        this.isDragging = true;
      }

      this.scrollContainer.scrollLeft = this.scrollLeft - walk;
    }

    initArrows() {
      if (!this.prevArrow || !this.nextArrow) return;

      this.prevArrow.addEventListener('click', () => {
        this.scrollByCards(-1);
      });

      this.nextArrow.addEventListener('click', () => {
        this.scrollByCards(1);
      });
    }

    scrollByCards(direction) {
      const container = this.scrollContainer;
      const card = container.querySelector('.multi-featured-products__card');
      if (!card) return;

      const cardWidth = card.offsetWidth;
      const gap = parseInt(getComputedStyle(container).gap) || 16;
      const scrollAmount = (cardWidth + gap) * direction;

      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }

    scrollToCenter() {
      const container = this.scrollContainer;
      const isMobile = window.innerWidth <= 749;
      const isCentered = container.classList.contains('multi-featured-products__scroll--centered');
      const hasOverflow = container.scrollWidth > container.clientWidth;

      if (isMobile && isCentered && hasOverflow) {
        // Scroll to the middle so the center cards are visible
        const middleScroll = (container.scrollWidth - container.clientWidth) / 2;
        container.scrollLeft = middleScroll;
      }
    }

    checkOverflow() {
      const container = this.scrollContainer;
      const hasOverflow = container.scrollWidth > container.clientWidth;

      // Toggle class for centered alignment behavior
      if (hasOverflow) {
        container.classList.add('has-overflow');
      } else {
        container.classList.remove('has-overflow');
      }

      // Hide arrows if no overflow
      if (this.prevArrow && this.nextArrow) {
        if (hasOverflow) {
          this.prevArrow.classList.remove('hidden');
          this.nextArrow.classList.remove('hidden');
        } else {
          this.prevArrow.classList.add('hidden');
          this.nextArrow.classList.add('hidden');
        }
      }
    }

    updateArrowStates() {
      if (!this.prevArrow || !this.nextArrow) return;

      const container = this.scrollContainer;
      const scrollLeft = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;

      // Disable prev arrow at start
      this.prevArrow.disabled = scrollLeft <= 1;

      // Disable next arrow at end
      this.nextArrow.disabled = scrollLeft >= maxScroll - 1;
    }

    updateFadeEdges() {
      if (!this.wrapper) return;

      const container = this.scrollContainer;
      const scrollLeft = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const hasOverflow = maxScroll > 0;

      // Show left fade when scrolled away from start
      if (hasOverflow && scrollLeft > 5) {
        this.wrapper.classList.add('has-overflow-left');
      } else {
        this.wrapper.classList.remove('has-overflow-left');
      }

      // Show right fade when not at the end
      if (hasOverflow && scrollLeft < maxScroll - 5) {
        this.wrapper.classList.add('has-overflow-right');
      } else {
        this.wrapper.classList.remove('has-overflow-right');
      }
    }

    debounce(fn, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  customElements.define('multi-featured-products', MultiFeaturedProducts);
}
